import { useState, useCallback, useRef } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import RichTextEditor from '#globalComponents/RichTextEditor/RichTextEditor';

import SendTestEmailModal from '../../components/common/SendTestEmailModal/SendTestEmailModal';

import AttachmentUploadField from './AttachmentUploadField';
import TemplatePreview from './TemplatePreview';
import TemplateVariablesPanel from './TemplateVariablesPanel';

const FIELD_LABEL = 'block text-xs font-medium text-bgBase-800 mb-1.5';

const EmailTemplateEditorModal = ({ template, onSave, onClose, isSaving }) => {
	const [name, setName] = useState(template?.name ?? '');
	const [emailSubject, setEmailSubject] = useState(template?.emailSubject ?? '');
	// Plain-text snapshot of subject + body, kept current so the fallback hint reacts to edits.
	const [contentText, setContentText] = useState(template?.emailContent ?? '');
	const [contentHtml, setContentHtml] = useState(template?.emailContent ?? '');
	const [mode, setMode] = useState('edit');
	const [previewRecipient, setPreviewRecipient] = useState(null);
	const [testOpen, setTestOpen] = useState(false);
	// Deferred attachment: the picked File is uploaded by the page AFTER the template is saved
	// (a new template has no id yet). `attachmentRemoved` marks an existing attachment for deletion.
	const [attachmentFile, setAttachmentFile] = useState(null);
	const [attachmentRemoved, setAttachmentRemoved] = useState(false);

	const editorRef = useRef(null);
	const subjectRef = useRef(null);
	// Placeholders go where the cursor was last: the subject line or the body.
	const [insertTarget, setInsertTarget] = useState('body');

	const insertVariable = useCallback(
		(token) => {
			if (insertTarget === 'body') {
				editorRef.current?.chain().focus().insertContent(token).run();
				return;
			}
			const input = subjectRef.current;
			const start = input?.selectionStart ?? emailSubject.length;
			const end = input?.selectionEnd ?? start;
			setEmailSubject(`${emailSubject.slice(0, start)}${token}${emailSubject.slice(end)}`);
			requestAnimationFrame(() => {
				input?.focus();
				input?.setSelectionRange(start + token.length, start + token.length);
			});
		},
		[insertTarget, emailSubject],
	);

	const displayedAttachment = attachmentFile
		? { fileName: attachmentFile.name, sizeBytes: attachmentFile.size, isNew: true }
		: !attachmentRemoved && template?.attachment
			? {
					fileName: template.attachment.originalFilename || 'Anhang',
					sizeBytes: template.attachment.sizeBytes,
					isNew: false,
				}
			: null;

	const handleSelectAttachment = useCallback((file) => {
		setAttachmentFile(file);
		setAttachmentRemoved(false);
	}, []);

	const handleRemoveAttachment = useCallback(() => {
		setAttachmentFile(null);
		setAttachmentRemoved(true);
	}, []);

	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault();
			if (!name.trim() || !emailSubject.trim()) return;
			const emailContent = editorRef.current?.getHTML() || '';
			onSave({
				name: name.trim(),
				emailSubject: emailSubject.trim(),
				emailContent,
				attachmentFile,
				removeAttachment: attachmentRemoved && !attachmentFile && Boolean(template?.attachment),
			});
		},
		[name, emailSubject, onSave, attachmentFile, attachmentRemoved, template],
	);

	return (
		<Modal isOpen onClose={onClose} size="xl" as="form" onSubmit={handleSubmit}>
			<Panel.Header
				onClose={onClose}
				subtitle="Wiederverwendbare E-Mail für Ihre Kampagnen. Platzhalter werden pro Empfänger ersetzt."
			>
				{template ? 'Template bearbeiten' : 'Neues E-Mail-Template'}
			</Panel.Header>

			<Panel.Body>
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
					<div className="space-y-4 min-w-0">
						<div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4">
							<div>
								<label className={FIELD_LABEL}>Template-Name</label>
								<TextInput
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="z.B. Follow-Up nach Telefonat"
									autoFocus={!template}
									required
								/>
							</div>
							<div>
								<label className={FIELD_LABEL}>Betreff</label>
								<TextInput
									ref={subjectRef}
									value={emailSubject}
									onChange={(e) => setEmailSubject(e.target.value)}
									onFocus={() => setInsertTarget('subject')}
									placeholder="Betreff der E-Mail"
									className={insertTarget === 'subject' ? 'ring-1 ring-primary-300' : ''}
									required
								/>
							</div>
						</div>

						<div>
							<div className="flex items-center justify-between mb-1.5">
								<label className="text-xs font-medium text-bgBase-800">E-Mail-Text</label>
								<div className="inline-flex rounded-control border border-bgBase-500 p-0.5 text-xs">
									{[
										['edit', 'Bearbeiten'],
										['preview', 'Vorschau'],
									].map(([key, label]) => (
										<button
											key={key}
											type="button"
											onClick={() => setMode(key)}
											className={`px-3 py-1 rounded-control transition-colors ${
												mode === key
													? 'bg-primary-500 text-white'
													: 'text-bgBase-800 hover:text-text'
											}`}
										>
											{label}
										</button>
									))}
								</div>
							</div>
							{/* The editor stays mounted while previewing so its content and history survive. */}
							<div
								className={mode === 'edit' ? '' : 'hidden'}
								onFocusCapture={() => setInsertTarget('body')}
							>
								<RichTextEditor
									initialContent={template?.emailContent ?? ''}
									placeholder="E-Mail-Text hier eingeben …"
									minHeightClass="min-h-[400px]"
									editorRef={editorRef}
									onUpdate={(html, text) => {
										setContentHtml(html);
										setContentText(text);
									}}
								/>
							</div>
							{mode === 'preview' && (
								<TemplatePreview
									emailSubject={emailSubject}
									emailContent={contentHtml}
									recipient={previewRecipient}
									onRecipientChange={setPreviewRecipient}
								/>
							)}
						</div>
					</div>

					<aside className="space-y-6 lg:border-l lg:border-bgBase-400 lg:pl-6">
						<TemplateVariablesPanel
							target={insertTarget}
							onInsert={insertVariable}
							text={`${emailSubject}\n${contentText}`}
						/>

						<div>
							<h4 className="text-sm font-semibold text-text mb-1.5">Anhang</h4>
							<AttachmentUploadField
								attachment={displayedAttachment}
								onSelect={handleSelectAttachment}
								onRemove={handleRemoveAttachment}
								disabled={isSaving}
							/>
							<p className="text-[11px] text-bgBase-800 mt-1.5">
								Wird an jede E-Mail dieses Templates angehängt.
							</p>
						</div>
					</aside>
				</div>
			</Panel.Body>

			<Panel.Footer className="justify-between">
				<Button type="button" variant="outline" onClick={() => setTestOpen(true)} size="sm">
					Test-E-Mail senden
				</Button>
				<div className="flex gap-2">
					<Button type="button" variant="outline" onClick={onClose} size="sm">
						Abbrechen
					</Button>
					<Button type="submit" size="sm" disabled={isSaving || !name.trim() || !emailSubject.trim()}>
						{isSaving ? 'Speichern...' : 'Speichern'}
					</Button>
				</div>
			</Panel.Footer>

			{testOpen && (
				<SendTestEmailModal
					isOpen
					onClose={() => setTestOpen(false)}
					template={{
						emailSubject,
						emailContent: contentHtml,
						...(template?.id && { emailTemplateId: template.id }),
					}}
					initialRecipient={previewRecipient}
				/>
			)}
		</Modal>
	);
};

EmailTemplateEditorModal.propTypes = {
	template: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
		emailSubject: PropTypes.string,
		emailContent: PropTypes.string,
		attachment: PropTypes.shape({
			id: PropTypes.string,
			originalFilename: PropTypes.string,
			sizeBytes: PropTypes.number,
			contentType: PropTypes.string,
		}),
	}),
	onSave: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	isSaving: PropTypes.bool,
};

export default EmailTemplateEditorModal;
